import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Button,
  Form,
  Input,
  Select,
  Row,
  Col,
  Tag,
  Radio,
  Steps,
  Alert,
  message,
} from 'antd';
import {
  CalendarOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  UserOutlined,
  MedicineBoxOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
  IdcardOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import CustomLoading from '../CustomLoading/CustomLoading';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useSelector } from 'react-redux';
import { addTurnPortalApi, updateTurnPortalApi } from '../../api/turn';
import { getSocialWorksApi } from '../../api/patient';
import { getServicesApi } from '../../api/service';
import { countryCodes } from '../../utils/constants';
import { useHistory } from 'react-router-dom';
import PatientNumberField from '../PatientNumberField/PatientNumberField';
import { usePortalRecaptcha } from '../../providers/RecaptchaProvider';
import {
  patientFacingTurnError,
  runTurnWithCaptchaRetry,
  withTurnSecurity,
} from '../../utils/portalTurnSecurity';
import './PortalTurnDrawer.scss';

const COUNTRY_CODES_WITH_AREA = ['+54', '+598'];

const STEPS = [
  { title: 'Tus datos' },
  { title: 'Cobertura' },
  { title: 'Confirmación' },
];

export default function PortalTurnDrawer({
  open,
  onClose,
  slot,
  selectedDay,
  doctor,
  scheduleConfig,
  turnId,
  turnUser,
  turnType,
  turns,
  size,
  selectedBranch,
  preselectedServiceId,
  setReload,
  initialValues,
  onDraftChange,
  setTurnDetail,
  initialStep = 0,
}) {
  const [step, setStep] = useState(initialStep);
  // savedStep1 persiste los valores del paso 1 aunque los Form.Items se desmonten en paso 2/3
  const [savedStep1, setSavedStep1] = useState(null);
  const [coverageMode, setCoverageMode] = useState('coverage');
  const [socialWorks, setSocialWorks] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [selectedService, setSelectedService] = useState(
    preselectedServiceId || null,
  );
  const [turnsSocialWorks, setTurnsSocialWorks] = useState({});
  const [coverageBlockedMsg, setCoverageBlockedMsg] = useState(null);
  const [confirmStatus, setConfirmStatus] = useState('idle'); // 'idle' | 'loading' | 'error'
  const [confirmError, setConfirmError] = useState(null);
  const [serviceDescExpanded, setServiceDescExpanded] = useState(false);
  const [loadingSocialWorks, setLoadingSocialWorks] = useState(true);

  const {
    tenantId,
    tenantTimezone,
    countryCode,
    direction,
    organizationPhone,
  } = useSelector((store) => store.tenantData);
  const [form] = Form.useForm();
  const history = useHistory();
  const { executeRecaptcha } = usePortalRecaptcha();

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setStep(initialStep);
      setCoverageMode('coverage');
      setCoverageBlockedMsg(null);
      setConfirmStatus('idle');
      setConfirmError(null);
      setSelectedService(preselectedServiceId || null);
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      if (initialValues) {
        form.setFieldsValue(initialValues);
        // Restaurar step 1 guardado si vienen datos previos
        if (initialValues.dni || initialValues.name) {
          setSavedStep1({
            dni: initialValues.dni,
            name: initialValues.name,
            email: initialValues.email,
            number: initialValues.number,
            countryCode: initialValues.countryCode,
            regionCode: initialValues.regionCode,
          });
        }
      } else {
        form.resetFields();
        setSavedStep1(null);
      }
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return (() => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    })
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar obras sociales y servicios
  useEffect(() => {
    if (!tenantId) return;
    getSocialWorksApi(tenantId)
      .then((r) => setSocialWorks(r?.socialWorks || []))
      .catch(() => {})
      .finally(() => setLoadingSocialWorks(false));
    getServicesApi(tenantId)
      .then((r) => setServicesData(r?.services || []))
      .catch(() => {});
  }, [tenantId]);

  // Sincronizar servicio preseleccionado
  useEffect(() => {
    if (preselectedServiceId) {
      setSelectedService(preselectedServiceId);
      form.setFieldsValue({ studiesTypes: preselectedServiceId });
    }
  }, [preselectedServiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calcular cupos por obra social para el día
  useEffect(() => {
    const count = turns?.reduce((acc, t) => {
      if (!t.softDelete) {
        acc[t.socialWork] = (acc[t.socialWork] || 0) + 1;
        acc.total =
          dayjs(slot?.hourStart).tz(tenantTimezone).unix() >
          dayjs().tz(tenantTimezone).add(2, 'hours').unix()
            ? (acc.total || 0) + 1
            : acc.total || 0;
      }
      return acc;
    }, {});
    setTurnsSocialWorks(count || { total: 0 });
    form.setFieldsValue({ socialWork: null });
  }, [turns, selectedDay, turnUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Filtro de obras sociales ────────────────────────────────────────────────
  const filterSocialWorks = (sws, sc) => {
    const base = (sws || []).filter(
      (sw) => sw.name?.toLowerCase() !== 'particular',
    );
    if (!sc?.coverageType || sc?.coverageType === 'acceptAll') return base;

    const day = dayjs(selectedDay).locale('es').format('dd');
    const configMap = (sc.socialWorksConfig || []).reduce((map, cfg) => {
      if (!map[cfg.socialWork]) map[cfg.socialWork] = [];
      map[cfg.socialWork].push(cfg);
      return map;
    }, {});

    const dataFilter = base.map((sw) => {
      const cfgs = configMap[sw.name] || false;

      // Excluida explícitamente: mostrar deshabilitada con mensaje
      if (cfgs && cfgs.every((c) => c.exclusion === true)) {
        return { ...sw, disabled: true, excluded: true, available: false };
      }

      const filteredConfig =
        cfgs?.length > 0 ? cfgs.find((c) => c?.days?.includes(day)) : false;
      const isEnabled = cfgs ? !!filteredConfig?.days?.includes(day) : true;
      const available =
        filteredConfig?.quantity != null
          ? filteredConfig.quantity - (turnsSocialWorks[sw.name] || 0)
          : false;
      return {
        ...sw,
        disabled: !isEnabled,
        excluded: false,
        daysForWork:
          filteredConfig === undefined
            ? { days: Array.from(new Set(cfgs?.flatMap((c) => c.days))) }
            : filteredConfig,
        available,
      };
    });

    if (sc?.coverageType === 'onlyListed') {
      // Solo mostrar las que están en la config (habilitadas o excluidas)
      return dataFilter.filter((sw) =>
        (sc.socialWorksConfig || []).some((c) => c.socialWork === sw.name),
      );
    }
    return dataFilter;
  };

  const filteredSocialWorks = useMemo(
    () => filterSocialWorks(socialWorks, scheduleConfig),
    [socialWorks, scheduleConfig, turnsSocialWorks, selectedDay], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ¿Este día solo acepta particular?
  const onlyParticularDay = useMemo(() => {
    // particularOnlyDays: días configurados explícitamente como "solo particular"
    if (scheduleConfig?.particularOnlyDays?.length > 0 && selectedDay) {
      const dayCode = dayjs(selectedDay).locale('es').format('dd');
      if (scheduleConfig.particularOnlyDays.includes(dayCode)) return true;
    }
    if (
      !scheduleConfig?.coverageType ||
      scheduleConfig?.coverageType === 'acceptAll'
    )
      return false;
    if (filteredSocialWorks.length === 0) return true;
    return filteredSocialWorks.every(
      (sw) =>
        sw.disabled || (typeof sw.available === 'number' && sw.available <= 0),
    );
  }, [
    filteredSocialWorks,
    scheduleConfig?.coverageType,
    scheduleConfig?.particularOnlyDays,
    selectedDay,
  ]);

  const hasNoSocialWork =
    !loadingSocialWorks &&
    (socialWorks.length === 0 ||
      (socialWorks.length === 1 &&
        socialWorks[0].name.toLowerCase() === 'particular'));
  const hasSocialWork = !hasNoSocialWork && !loadingSocialWorks;

  // Auto-switch a particular si el día no tiene coberturas
  useEffect(() => {
    if (loadingSocialWorks) return;
    if (onlyParticularDay || hasNoSocialWork) {
      setCoverageMode('particular');
      setCoverageBlockedMsg(null);
    }
  }, [onlyParticularDay, hasNoSocialWork, loadingSocialWorks]);

  // ─── Verificación inline de cobertura ────────────────────────────────────────
  const checkCoverageForDay = (swName) => {
    if (coverageMode === 'particular') {
      setCoverageBlockedMsg(null);
      return true;
    }
    if (!swName) return false;
    const sw = filteredSocialWorks.find((s) => s.name === swName);
    if (!sw) return true;
    if (sw.excluded) {
      setCoverageBlockedMsg(
        `${swName} no tiene convenio en este consultorio. Podés ir como particular.`,
      );
      return false;
    }
    if (sw.disabled) {
      setCoverageBlockedMsg(
        `${swName} solo se atiende los: ${sw.daysForWork?.days?.join(', ')}. Podés elegir otro día o ir como particular.`,
      );
      return false;
    }
    if (typeof sw.available === 'number' && sw.available <= 0) {
      setCoverageBlockedMsg(
        `No hay cupo de ${swName} para este día. Podés ir como particular o elegir otro horario.`,
      );
      return false;
    }
    setCoverageBlockedMsg(null);
    return true;
  };

  // ─── Datos para el header ────────────────────────────────────────────────────
  const dayLabel = selectedDay
    ? `${dayjs(selectedDay).format('dddd D')} de ${dayjs(selectedDay).format('MMMM')}`
    : '';
  const hourLabel = slot?.hourStart
    ? `${dayjs(slot.hourStart).tz(tenantTimezone).format('HH:mm')} hs`
    : '';
  const doctorName =
    doctor?.name && doctor?.lastname
      ? `${doctor.name} ${doctor.lastname}`
      : doctor?.name || '';

  // ─── Servicios ───────────────────────────────────────────────────────────────
  const serviceIds =
    scheduleConfig?.serviceConfigMode === 'perBlock'
      ? scheduleConfig?.hours?.flatMap((h) => h.serviceIds || []) || []
      : scheduleConfig?.service || [];
  const availablePublicServices = (servicesData || []).filter(
    (s) => s?.public && serviceIds.some((sid) => String(sid) === String(s._id)),
  );
  const showServiceField =
    !preselectedServiceId && availablePublicServices.length > 0;
  const preselectedServiceName = preselectedServiceId
    ? servicesData?.find((s) => (s._id || s.id) === preselectedServiceId)?.name
    : null;

  // ─── Submit (paso 1 → paso 2 → paso 3 con request) ──────────────────────────
  const handleConfirm = async () => {
    const step2Fields = ['studiesTypes'];
    if (coverageMode === 'coverage') step2Fields.push('socialWork');
    try {
      await form.validateFields(step2Fields);
    } catch {
      return;
    }
    if (coverageMode === 'coverage') {
      const swName = form.getFieldValue('socialWork');
      if (!swName) {
        setCoverageBlockedMsg('Seleccioná tu cobertura');
        return;
      }
      if (!checkCoverageForDay(swName)) return;
    }

    // Ir al paso 3 (pantalla de procesando)
    setStep(2);
    setConfirmStatus('loading');

    // Usar savedStep1 para datos del paso 1 (evita perder valores si Form.Items están desmontados)
    const step2Values = form.getFieldsValue([
      'socialWork',
      'studiesTypes',
      'description',
    ]);
    const identity = savedStep1 || {};
    const socialWork =
      coverageMode === 'particular'
        ? 'PARTICULAR'
        : step2Values.socialWork || '';
    const useAreaCode = COUNTRY_CODES_WITH_AREA.includes(identity?.countryCode);
    const notificationPhone = {
      countryCode: identity?.countryCode,
      regionCode: useAreaCode ? identity?.regionCode : undefined,
      number: identity?.number,
    };
    const serviceId = preselectedServiceId || step2Values?.studiesTypes;
    const services = (servicesData || []).filter((s) => serviceId === s._id);
    const price =
      services.reduce((acc, s) => acc + parseFloat(s.price || 0), 0) || null;

    const params = {
      turnDetail: {
        hour: dayjs(slot?.hourStart).tz(tenantTimezone),
        services: services.length ? services : null,
        dni: identity.dni,
      },
      doctor,
      turnType,
      description:
        scheduleConfig?.descriptionVisible && scheduleConfig?.description,
      dni: identity.dni,
    };

    const sizeSlot = slot?.sizeSlot || size;

    const dataTurn = {
      hourStart: slot?.hourStart,
      socialWork,
      name: identity.name,
      dni: identity.dni,
      notificationPhone,
      email: identity.email,
      price,
      portal: true,
      studies: services.length ? services : null,
      description: step2Values?.description,
      branchId: selectedBranch?._id || selectedBranch?.id,
      // requerido tanto para addTurnPortal (outer data) como updateTurnPortal (body directo)
      user: turnUser,
      turnType,
      sizeSlot,
    };

    try {
      const result = await runTurnWithCaptchaRetry({
        patientNumber: form.getFieldValue('patientNumber'),
        executeRecaptcha,
        action: turnId ? 'update_turn' : 'add_turn',
        send: (security) =>
          turnId
            ? updateTurnPortalApi({
                tenantId,
                turnId,
                dataTurn: withTurnSecurity(dataTurn, security),
              })
            : addTurnPortalApi({
                tenantId,
                data: withTurnSecurity(
                  {
                    date: dayjs(selectedDay).format('YYYY-MM-DD'),
                    user: turnUser,
                    sizeSlot,
                    turnType,
                    dataTurn,
                  },
                  security,
                ),
              }),
      });

      if (result.blocked) {
        setConfirmStatus('error');
        setConfirmError(result.message);
        return;
      }

      const response = result.response;

      if (!response || response?.code !== 200) {
        setConfirmStatus('error');
        setConfirmError(
          patientFacingTurnError(response, 'El turno ya fue ocupado. Elegí otro horario.'),
        );
        if (typeof setReload === 'function') setReload(true);
      } else {
        setConfirmStatus('idle');
        const bookedAtUnix = Math.floor(Date.now() / 1000);
        params.cancelLink = response?.cancelLink;
        params.direction = response?.direction || direction;
        params.phone = response?.phone || organizationPhone;
        params.bookedAtUnix = bookedAtUnix;
        params.turnDetail.bookedAtUnix = bookedAtUnix;
        params.voucherIncomplete = Boolean(response?.voucherIncomplete);
        message.success(response.message || '¡Turno reservado!', 2);
        if (response?.voucherIncomplete) {
          message.warning(
            'El turno se guardó, pero no se pudo generar el código para cancelar.',
            6,
          );
        }
        onClose();
        if (setTurnDetail) {
          setTurnDetail({
            hour: dayjs(slot?.hourStart).tz(tenantTimezone),
            services: services.length ? services : null,
            cancelLink: response?.cancelLink,
            direction: response?.direction || direction,
            phone: response?.phone || organizationPhone,
            dni: identity.dni,
            bookedAtUnix,
          });
        } else {
          history.push('/confirm-turn', params);
        }
      }
    } catch {
      setConfirmStatus('error');
      setConfirmError('Error al conectar con el servidor. Intentá de nuevo.');
    }
  };

  // ─── Resumen del paso 1 (para mostrar en paso 2) ──────────────────────────────
  const renderStep1Summary = () => {
    const v = savedStep1;
    if (!v?.dni && !v?.name) return null;
    const phone = [v.countryCode, v.regionCode, v.number]
      .filter(Boolean)
      .join(' ');
    return (
      <div className="ptd-summary">
        {v.name && (
          <span className="ptd-summary__item">
            <IdcardOutlined /> {v.name}
            {v.dni ? ` · DNI ${v.dni}` : ''}
          </span>
        )}
        {v.email && (
          <span className="ptd-summary__item">
            <MailOutlined /> {v.email}
          </span>
        )}
        {v.number && (
          <span className="ptd-summary__item">
            <PhoneOutlined /> {phone}
          </span>
        )}
      </div>
    );
  };

  // ─── Paso 1: datos de identidad ──────────────────────────────────────────────
  const renderStep1 = () => (
    <div className="ptd-step">
      <p className="ptd-step__subtitle">Completá tus datos para continuar</p>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={8}>
          <Form.Item
            name="dni"
            label={countryCode === '+54' ? 'DNI' : 'DNI / Cédula'}
            labelCol={{ span: 24 }}
            getValueFromEvent={(event) =>
              String(event?.target?.value ?? '').replace(/\D/g, '')
            }
            rules={[
              {
                validator: (_, value) => {
                  const text = String(value ?? '');
                  if (!text || !/^\d+$/.test(text)) {
                    return Promise.reject(new Error('Ingresá el DNI sin puntos'));
                  }
                  if (text.length < 5) {
                    return Promise.reject(new Error('DNI inválido'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              className="portal-dni-input"
              placeholder="34256389"
              type="number"
              inputMode="numeric"
              size="large"
              onKeyDown={(event) => {
                if (['.', ',', 'e', 'E', '+', '-'].includes(event.key)) {
                  event.preventDefault();
                }
              }}
              onBeforeInput={(event) => {
                if (event.data && /\D/.test(event.data)) {
                  event.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>
        <PatientNumberField />
        <Col xs={24} sm={16}>
          <Form.Item
            name="name"
            label="Apellido y Nombre"
            labelCol={{ span: 24 }}
            rules={[
              { required: true, message: 'Ingresá tu apellido y nombre' },
            ]}
          >
            <Input
              placeholder="García Juan"
              style={{ textTransform: 'capitalize' }}
              size="large"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="email"
            label="Correo electrónico"
            labelCol={{ span: 24 }}
            rules={[
              {
                required: true,
                type: 'email',
                message: 'Ingresá un email válido',
              },
            ]}
            tooltip="Te enviaremos la confirmación del turno por mail"
          >
            <Input
              placeholder="ejemplo@mail.com"
              inputMode="email"
              size="large"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Teléfono de contacto" labelCol={{ span: 24 }}>
            <Form.Item
              noStyle
              shouldUpdate={(p, c) => p.countryCode !== c.countryCode}
            >
              {({ getFieldValue }) => {
                const selected = getFieldValue('countryCode') || countryCode;
                const useArea = COUNTRY_CODES_WITH_AREA.includes(selected);
                return (
                  <Row gutter={4}>
                    <Col xs={useArea ? 8 : 10}>
                      <Form.Item
                        name="countryCode"
                        noStyle
                        initialValue={countryCode}
                      >
                        <Select
                          size="large"
                          onChange={() =>
                            form.setFieldsValue({ regionCode: undefined })
                          }
                        >
                          {countryCodes?.map((c) => (
                            <Select.Option
                              key={c.dial_code}
                              value={c.dial_code}
                            >
                              {c.emoji} {c.dial_code}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    {useArea && (
                      <Col xs={7}>
                        <Form.Item
                          name="regionCode"
                          noStyle
                          rules={[
                            {
                              validator: (_, v) => {
                                const code = String(v ?? '').trim();
                                if (!code)
                                  return Promise.reject('Cód. área requerido');
                                if (!/^\d{2,5}$/.test(code))
                                  return Promise.reject('Inválido');
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            placeholder="Cód área"
                            maxLength={5}
                            inputMode="numeric"
                            size="large"
                          />
                        </Form.Item>
                      </Col>
                    )}
                    <Col xs={useArea ? 9 : 14}>
                      <Form.Item
                        name="number"
                        noStyle
                        rules={[
                          {
                            validator: (_, v) => {
                              const num = String(v ?? '').trim();
                              if (!num)
                                return Promise.reject('Ingresá el número');
                              if (!/^\d{6,15}$/.test(num))
                                return Promise.reject('Número inválido');
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          addonBefore={selected === '+54' ? '15' : undefined}
                          placeholder={useArea ? 'Nro Tel.' : 'Ej: 71234567'}
                          maxLength={useArea && selected === '+54' ? 8 : 15}
                          inputMode="numeric"
                          size="large"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
          </Form.Item>
        </Col>
      </Row>
      <div className="ptd-step__actions">
        <Button
          type="primary"
          size="large"
          block
          onClick={async () => {
            try {
              await form.validateFields([
                'dni',
                'name',
                'email',
                'countryCode',
                'regionCode',
                'number',
              ]);
              const s1 = form.getFieldsValue([
                'dni',
                'name',
                'email',
                'number',
                'countryCode',
                'regionCode',
              ]);
              setSavedStep1(s1);
              if (typeof onDraftChange === 'function') onDraftChange(s1);
              setStep(1);
            } catch {}
          }}
        >
          Continuar →
        </Button>
      </div>
    </div>
  );

  // ─── Tarjeta de detalle del servicio ─────────────────────────────────────────
  const DESC_LIMIT = 120;
  const renderServiceDetail = (svc) => {
    if (!svc) return null;
    const hasDesc = svc.description && svc.description.trim().length > 0;
    const isLong = hasDesc && svc.description.trim().length > DESC_LIMIT;
    const displayDesc =
      isLong && !serviceDescExpanded
        ? svc.description.trim().slice(0, DESC_LIMIT) + '…'
        : svc.description?.trim();

    return (
      <div className="ptd-service-detail">
        <div className="ptd-service-detail__row">
          <span className="ptd-service-detail__name">{svc.name}</span>
          {svc.price ? (
            <span className="ptd-service-detail__price">
              {Number(svc.price).toLocaleString('es-AR', {
                style: 'currency',
                currency: 'ARS',
                minimumFractionDigits: 0,
              })}
            </span>
          ) : null}
        </div>
        {hasDesc && (
          <p className="ptd-service-detail__desc">
            {displayDesc}
            {isLong && (
              <button
                className="ptd-service-detail__toggle"
                onClick={() => setServiceDescExpanded((v) => !v)}
              >
                {serviceDescExpanded ? ' Ver menos' : ' Ver más'}
              </button>
            )}
          </p>
        )}
      </div>
    );
  };

  // ─── Paso 2: cobertura y servicio ────────────────────────────────────────────
  const renderStep2 = () => (
    <div className="ptd-step">
      {/* Resumen del paso 1 */}
      {renderStep1Summary()}

      <p className="ptd-step__subtitle">¿Cómo querés atenderte?</p>

      {/* Toggle cobertura / particular */}
      <div className="ptd-coverage-toggle">
        <button
          className={`ptd-coverage-btn ${onlyParticularDay || hasNoSocialWork ? 'ptd-coverage-btn--disabled' : coverageMode === 'coverage' ? 'active' : ''}`}
          disabled={loadingSocialWorks || onlyParticularDay || hasNoSocialWork}
          onClick={() => {
            if (onlyParticularDay) return;
            setCoverageMode('coverage');
            setCoverageBlockedMsg(null);
          }}
        >
          <MedicineBoxOutlined />
          <span>Con mi cobertura</span>
          {(onlyParticularDay || hasNoSocialWork) && (
            <span className="ptd-coverage-btn__badge">
              {hasNoSocialWork ? 'No disponible' : 'No disponible hoy'}
            </span>
          )}
        </button>
        <button
          className={`ptd-coverage-btn ${coverageMode === 'particular' ? 'active' : ''}`}
          onClick={() => {
            setCoverageMode('particular');
            setCoverageBlockedMsg(null);
            form.setFieldsValue({ socialWork: null });
          }}
        >
          <UserOutlined />
          <span>Particular</span>
        </button>
      </div>

      {/* Aviso día solo particular */}
      {onlyParticularDay && hasSocialWork && (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message="Este día solo se atiende de forma particular"
          description="No hay cobertura disponible para este horario. Podés continuar como particular o elegir otro día si preferís atenderte con tu obra social."
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={onClose}>
              Elegir otro día
            </Button>
          }
        />
      )}

      {/* Select de obra social */}
      {coverageMode === 'coverage' && !onlyParticularDay && hasSocialWork && (
        <Form.Item
          name="socialWork"
          label="Obra social / Seguro"
          labelCol={{ span: 24 }}
          rules={[{ required: true, message: 'Seleccioná tu cobertura' }]}
          style={{ marginTop: 16 }}
        >
          <Select
            placeholder="Seleccioná tu obra social"
            showSearch
            optionFilterProp="value"
            size="large"
            onChange={(val) => checkCoverageForDay(val)}
          >
            {filteredSocialWorks.map((sw) => (
              <Select.Option
                key={sw.name}
                value={sw.name}
                disabled={
                  sw?.disabled ||
                  (typeof sw?.available === 'number' && sw?.available <= 0)
                }
              >
                <span style={{ textTransform: 'uppercase' }}>{sw.name}</span>
                {(() => {
                  if (sw?.excluded)
                    return (
                      <Tag color="default" style={{ marginLeft: 8 }}>
                        Sin convenio
                      </Tag>
                    );
                  if (sw?.disabled)
                    return (
                      <Tag color="default" style={{ marginLeft: 8 }}>
                        Solo los: {sw?.daysForWork?.days?.join(', ')}
                      </Tag>
                    );
                  if (typeof sw?.available === 'number' && sw?.available <= 0)
                    return (
                      <Tag color="red" style={{ marginLeft: 8 }}>
                        Sin cupo
                      </Tag>
                    );
                  return (
                    <Tag
                      color={
                        sw?.available > 5 || !sw?.available
                          ? 'success'
                          : sw?.available >= 3
                            ? 'gold'
                            : 'volcano'
                      }
                      style={{ marginLeft: 8 }}
                    >
                      {sw?.available > 5 || !sw?.available
                        ? 'Disponible'
                        : `Disponibles: ${sw.available}`}
                    </Tag>
                  );
                })()}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {/* Alert cobertura bloqueada */}
      {coverageBlockedMsg && (
        <Alert
          type="warning"
          showIcon
          message={coverageBlockedMsg}
          style={{ marginBottom: 16 }}
          action={
            <div className="ptd-blocked-actions">
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => {
                  setCoverageMode('particular');
                  setCoverageBlockedMsg(null);
                  form.setFieldsValue({ socialWork: null });
                }}
              >
                Ir como particular
              </Button>
              <Button size="small" onClick={onClose}>
                Otro horario
              </Button>
            </div>
          }
        />
      )}

      {/* Servicio preseleccionado (perBlock) */}
      {preselectedServiceId &&
        (() => {
          const svc = servicesData?.find(
            (s) => (s._id || s.id) === preselectedServiceId,
          );
          return svc ? renderServiceDetail(svc) : null;
        })()}

      {showServiceField && (
        <>
          <Form.Item
            name="studiesTypes"
            label="Servicio"
            labelCol={{ span: 24 }}
            rules={[{ required: true, message: 'Seleccioná un servicio' }]}
          >
            <Select
              placeholder="Seleccioná el servicio"
              size="large"
              onChange={(v) => {
                setSelectedService(v);
                setServiceDescExpanded(false);
              }}
            >
              {availablePublicServices.map((s) => (
                <Select.Option key={s._id} value={s._id}>
                  {s.name}
                  {s.price
                    ? ` — ${Number(s.price).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 })}`
                    : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {/* Detalle del servicio seleccionado */}
          {selectedService &&
            (() => {
              const svc = availablePublicServices.find(
                (s) => s._id === selectedService,
              );
              return svc ? renderServiceDetail(svc) : null;
            })()}
        </>
      )}

      {turnType === 'medico' && !showServiceField && !preselectedServiceId && (
        <Form.Item
          name="description"
          label="Tipo de consulta (opcional)"
          labelCol={{ span: 24 }}
        >
          <Radio.Group>
            <Radio value="Primera Vez">Primera Vez</Radio>
            <Radio value="Consulta">Consulta</Radio>
            <Radio value="Consulta + Practica">Consulta + Práctica</Radio>
          </Radio.Group>
        </Form.Item>
      )}

      <div className="ptd-step__actions ptd-step__actions--two">
        <Button
          size="large"
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            setCoverageBlockedMsg(null);
            setStep(0);
          }}
        >
          Volver
        </Button>
        <Button
          type="primary"
          size="large"
          disabled={!!coverageBlockedMsg}
          onClick={handleConfirm}
          style={{ flex: 1 }}
        >
          Confirmar turno
        </Button>
      </div>

      <div className="ptd-other-slot">
        <button className="ptd-other-slot__btn" onClick={onClose}>
          ¿Preferís otro horario? Buscarlo
        </button>
      </div>
    </div>
  );

  // ─── Paso 3: procesando / error ──────────────────────────────────────────────
  const renderStep3 = () => (
    <div className="ptd-step ptd-step--confirm">
      {confirmStatus === 'loading' && <CustomLoading loading={true} />}
      {confirmStatus === 'error' && (
        <>
          <div className="ptd-confirm__icon ptd-confirm__icon--error">✕</div>
          <p className="ptd-confirm__title">No pudimos reservar el turno</p>
          <p className="ptd-confirm__sub">{confirmError}</p>
          <div className="ptd-confirm__actions">
            <Button onClick={onClose} size="large" block>
              Elegir otro horario
            </Button>
            <Button
              type="primary"
              size="large"
              block
              onClick={() => {
                setConfirmStatus('idle');
                setConfirmError(null);
                setStep(1);
              }}
            >
              Reintentar
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const isProcessing = step === 2 && confirmStatus === 'loading';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="bottom"
      height="auto"
      className="portal-turn-drawer"
      closable={false}
      maskClosable={!isProcessing}
      title={null}
      footer={null}
      destroyOnClose={false}
    >
      {/* Header fijo con resumen del turno */}
      <div className="ptd-header">
        <div className="ptd-header__top">
          <div className="ptd-header__info">
            <div className="ptd-header__row">
              <CalendarOutlined className="ptd-header__icon" />
              <span
                className="ptd-header__datetime"
                style={{ textTransform: 'capitalize' }}
              >
                {dayLabel} — {hourLabel}
              </span>
            </div>
            <div className="ptd-header__row">
              <UserOutlined className="ptd-header__icon" />
              <span className="ptd-header__doctor">{doctorName}</span>
              {doctor?.medicalSpeciality && (
                <span className="ptd-header__speciality">
                  · {doctor.medicalSpeciality}
                </span>
              )}
            </div>
            {selectedBranch?.name && (
              <div className="ptd-header__row">
                <EnvironmentOutlined className="ptd-header__icon" />
                <span className="ptd-header__branch">
                  {selectedBranch.name}
                </span>
              </div>
            )}
          </div>
          {!isProcessing && (
            <button
              className="ptd-header__close"
              onClick={onClose}
              aria-label="Cerrar"
            >
              <CloseOutlined />
            </button>
          )}
        </div>
      </div>

      {/* Stepper centrado */}
      <div className="ptd-steps-wrapper">
        <Steps
          current={step}
          size="small"
          className="ptd-steps"
          items={STEPS}
        />
      </div>

      {/* Formulario (se monta una sola vez) */}
      <Form
        form={form}
        onValuesChange={(_, all) => {
          // Siempre mergear con savedStep1 para no perder datos al cambiar de paso
          if (typeof onDraftChange === 'function')
            onDraftChange({ ...savedStep1, ...all });
        }}
      >
        {step === 0 && renderStep1()}
        {step === 1 && renderStep2()}
        {step === 2 && renderStep3()}
      </Form>
    </Drawer>
  );
}
